import React from 'react';

import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    Constants,
    HistoryRoutes,
    Messages,
    SnackbarVariants,
    StatusRegisterMessages,
    ViewKeys,
} from 'commons/constants';
import {useAlert, useSpinner} from 'hooks';
import {MainContent} from 'layouts/commons';
import {useAnalytics} from 'hooks/AnalyticsHook';
import {useUserContext} from 'context/user/UserContext';
import {ContractStatus, EfeModalMsg, EfeSave} from 'components';
import {ClientService, DocumentServices} from 'services';
import {getBase64Extension, nextStep} from 'commons/utils/Functions';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import DigitalizedSignatureForm from '../digitalized-sign-form/DigitalizedSignatureForm';
import {EfeResponse, ExpedientResponse, UploadDocumentServiceRequest} from 'commons/models';
import {DigitalizedSignatureValues} from 'commons/models/modules/content/DigitalizedSignatureValues';

function MigratedUser() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {sendPageView} = useAnalytics();
    const {enqueueSnackbar} = useSnackbar();
    const {user, updateUser} = useUserContext();
    const {expedientId, fatherKycId, menu} = useMenuContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const [isLoading, setIsLoading] = React.useState(false);
    const [showContract, setShowContract] = React.useState(false);
    const [userBlocked, setUserBlocked] = React.useState<boolean>(true);
    const [message, setMessage] = React.useState<string>(
        StatusRegisterMessages.CONTRACT_IN_REVISION
    );

    async function getExpedientStatus(): Promise<string> {
        let state = '-1';
        try {
            const response: EfeResponse<ExpedientResponse> = await ClientService.getExpedientStatus(
                expedientId
            );
            if (response && response.data) {
                const data: ExpedientResponse = response.data;
                state = data.stateKey;
            } else {
                showAlertError(undefined, Messages.COMMON_ERROR);
            }
        } catch (error) {
            showAlertError(error, Messages.COMMON_ERROR);
        }
        return state;
    }

    async function sendSignature(value: DigitalizedSignatureValues) {
        setIsLoading(true);
        try {
            const fileConverted = await (await fetch(value.signature)).blob();
            const extension = getBase64Extension(value.signature);
            const request: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_FIRMA_MIGRADO,
                expedientId: expedientId,
                file: fileConverted,
                kycId: fatherKycId,
                filename: `${Constants.DOC_FIRMA_MIGRADO}.${extension}`,
            };
            const response: EfeResponse<any> = await DocumentServices.updateDocument(request);
            if (response && response.data) {
                setIsLoading(false);
                enqueueSnackbar(Messages.FILE_UPLOAD_OK, {
                    variant: SnackbarVariants.SUCCESS,
                });
                if (
                    user.isMigrated &&
                    flow.data.expandKey === ViewKeys.V_FIRMA_TERMINOS_CONDICIONES_MIGRADO
                ) {
                    setShowContract(true);
                    const signedResult = await ClientService.signTermsConditions(expedientId);
                    if (signedResult && signedResult.data) {
                        updateUser({...user, contractService: true});
                    }
                } else {
                    history.push(nextStep(flow, menu, true));
                }
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    async function handleSubmit() {
        setIsLoading(false);
        history.push(HistoryRoutes.REGISTER);
    }

    React.useEffect(() => {
        if (fatherKycId > 0 && expedientId) {
            (async function () {
                setIsLoading(true);
                if (Constants.AML_ONBOARDING_ID === (await getExpedientStatus())) {
                    setUserBlocked(false);
                    setMessage(StatusRegisterMessages.PROCESS_OK);
                    sendPageView(HistoryRoutes.COMPLETED_REGISTER);
                }
                setIsLoading(false);
            })();
        }
    }, []);

    React.useEffect(() => {
        setIsLoading(true);
        if (
            !user.isMigrated &&
            flow.data.expandKey === ViewKeys.V_FIRMA_TERMINOS_CONDICIONES_MIGRADO
        ) {
            console.error('Usuario no migrado en pantalla de migrados');
            history.push(HistoryRoutes.REGISTER);
        } else {
            if (
                user.isMigrated &&
                user.contractService &&
                flow.data.expandKey === ViewKeys.V_FIRMA_TERMINOS_CONDICIONES_MIGRADO
            ) {
                setShowContract(true);
            }
            setIsLoading(false);
        }
    }, []);

    return (
        <>
            <MainContent>
                {showAlert && (
                    <EfeModalMsg
                        error={genericError}
                        open={showAlert}
                        defaultError={defaultError}
                        handleAction={closeAlert}
                    />
                )}
                {isLoading && useSpinner(isLoading)}
                {showContract ? (
                    <ContractStatus
                        message={message}
                        userBlocked={userBlocked}
                        dashboardBlocked={true}
                        hideDashboard={true}
                    />
                ) : (
                    <DigitalizedSignatureForm submitHandler={sendSignature} />
                )}
            </MainContent>
            <EfeSave
                flow={flow}
                menu={menu}
                disabled={showContract}
                updateModuleStep={updateModuleStep}
                overrideHandler={handleSubmit}
            />
        </>
    );
}

export default MigratedUser;
